import React, { useEffect, useState } from 'react';
import { Modal, Tabs, Tab, OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { PeriodicReportingType } from '../PeriodicReporting';
import DataHeader from 'components/DataHeader';
import PeriodicReportingExternalCosts from './PeriodicReportingExternalCosts';
import PeriodicReportingInternalCosts from './PeriodicReportingInternalCosts';
import PeriodicReportingRecordAnalysis from './PeriodicReportingRecordAnalysis';
import UploadDocument from 'components/form/upload/uploadDocuments';
import PeriodicReportingSummary from './PeriodicReportingSummary';
import { useLocation } from 'react-router-dom-v5-compat';
import PeriodicReportingIndirectCosts from './PeriodicReportingIndirectCosts';
import { IcoEdit } from 'icone';
import { getStatementAmountsByProjectIdAndStatementId } from 'lib/api_ts/periodicReportingApi';
import { formatToDecimals } from 'lib/helpers/formatting';
import PeriodicReportingValidateModal from './PeriodicReportingValidateModal';
import PeriodicReportingSettleModal from './PeriodicReportingSettleModal';
import PeriodicReportingStatus from './PeriodicReportingStatus';
import CheckDocTab from 'components/projectStatements/CheckDocTab';
import MassiveReportingDownload from '@components/form/MassiveReportingDownload';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface PeriodicReportingUpdateModalProps {
	data: PeriodicReportingType;
	fetchData: () => void;
	fetchTotals: () => void;
	isCoordinator: boolean;
	state: 'DRAFT' | 'VALIDATED';
	isEnabledToView: boolean;
	isBeneficiary: boolean;
	isProjectWp: boolean;
	userCompanies?: number[];
	projectIndirectCostsPerc: number;
}

const PeriodicReportingUpdateModal: React.FC<PeriodicReportingUpdateModalProps> = ({
	data,
	fetchData,
	fetchTotals,
	isCoordinator,
	state,
	isEnabledToView,
	isBeneficiary,
	isProjectWp,
	userCompanies,
	projectIndirectCostsPerc,
}) => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	const initialActiveKey = state === 'VALIDATED' ? 'status' : 'externalCosts';
	const [activeKey, setActiveKey] = useState(initialActiveKey);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isSettleAllModalOpen, setIsSettleAllModalOpen] = useState(false);
	const [invoiceId, setInvoiceId] = useState('');
	const [entity, setEntity] = useState('');
	const [rc, setRc] = useState<string[]>([]);

	const [inputValues, setInputValues] = useState({
		contextAndObjectives: data.contextAndObjectives || '',
		expectedOutcomes: data.expectedOutcomes || '',
		workPerformedAndResults: data.workPerformedAndResults || '',
		projectPolicyRelevance: data.projectPolicyRelevance || '',
		note: data.note || '',
	});

	useEffect(() => {
		if (activeKey !== 'records') {
			setInvoiceId('');
			setEntity('');
			setRc([]);
		}
	}, [activeKey]);

	const [selectedIds, setSelectedIds] = useState<number[]>([]);

	const queryClient = useQueryClient();

	const fetchAmounts = () => {
		queryClient.invalidateQueries({ queryKey: ['statementAmount', projectId, data.id] });
	};

	const { data: amounts, isPending: isPendingAmounts } = useQuery({
		queryKey: ['statementAmount', projectId, data.id, isModalOpen],
		queryFn: async () => getStatementAmountsByProjectIdAndStatementId(projectId, data.id),
		enabled: projectId !== undefined && data?.id !== undefined && isModalOpen,
	});

	const headerElements = [
		{
			name: <span className="text-nowrap">ID</span>,
			value: data.periodicReportingNumber,
		},
		{
			name: <span className="text-nowrap">Importo rendicontato</span>,
			value: !isPendingAmounts ? formatToDecimals(amounts.total) + ' €' : null,
		},
		{
			name: <span className="text-nowrap">{isProjectWp ? 'Contributo massimo ottenibile' : 'Contributo richiesto'}</span>,
			value: !isPendingAmounts ? formatToDecimals(amounts.requestedAmount) + ' €' : null,
		},
	];

	const tabs = [
		...(state === 'VALIDATED'
			? [
					{
						eventKey: 'status',
						title: 'Status',
						content: (
							<PeriodicReportingStatus data={data} isBeneficiary={isBeneficiary} isEnabledToView={isEnabledToView} isWP={isProjectWp} />
						),
					},
					{
						eventKey: 'integration',
						title: 'Integrazione',
						content: (
							<CheckDocTab
								detail={data}
								tab={activeKey}
								conditionalitiesFilterCodes={['Integrazione']}
								isValidated={state === 'VALIDATED'}
								handleChanges={() => {}}
								setDetail={() => {}}
								isBeneficiary={isBeneficiary}
								isCoordinator={isCoordinator}
								isEnabledToView={isEnabledToView}
								isProjectWp={isProjectWp}
							/>
						),
					},
				]
			: []),
		{
			eventKey: 'externalCosts',
			title: 'Costi esterni',
			content: (
				<PeriodicReportingExternalCosts
					data={data}
					amounts={amounts}
					refreshAmounts={fetchAmounts}
					isCoordinator={isCoordinator}
					isEnabledToView={isEnabledToView}
					state={state}
				/>
			),
		},
		{
			eventKey: 'internalCosts',
			title: 'Costi interni',
			content: (
				<PeriodicReportingInternalCosts
					data={data}
					isCoordinator={isCoordinator}
					isEnabledToView={isEnabledToView}
					refreshAmounts={fetchAmounts}
					state={state}
				/>
			),
		},
		{
			eventKey: 'records',
			title: 'Entità da validare',
			content: (
				<PeriodicReportingRecordAnalysis
					data={data}
					isCoordinator={isCoordinator}
					isEnabledToView={isEnabledToView}
					state={state}
					selectedIds={selectedIds}
					setSelectedIds={setSelectedIds}
					entity={entity}
					setEntity={setEntity}
					invoiceId={invoiceId}
					setInvoiceId={setInvoiceId}
					rc={rc}
					setRc={setRc}
				/>
			),
		},
		{
			eventKey: 'indirectCosts',
			title: 'Costi indiretti',
			content: <PeriodicReportingIndirectCosts data={data} projectIndirectCostsPerc={projectIndirectCostsPerc} />,
		},
		{
			eventKey: 'attachments',
			title: 'Allegati',
			content: (
				<UploadDocument
					displayName={true}
					filterBySection={['PR']}
					entity="statements"
					projectId={projectId}
					entityId={data.id}
					enableUpload={true}
					wpCompanyManagement={{
						isCoordinator: isCoordinator,
						isEnabledToView: isEnabledToView,
						isBeneficiary: isBeneficiary,
						filterDocsByUserCompanyIds: userCompanies,
					}}
					TitleButton={<MassiveReportingDownload projectId={projectId} statementId={data.id} />}
				/>
			),
		},
		// ...(isCoordinator
		// 	? [
		{
			eventKey: 'summary',
			title: 'Summary',
			content: (
				<PeriodicReportingSummary
					data={data}
					state={state}
					activeKey={activeKey}
					inputValues={inputValues}
					setInputValues={setInputValues}
					isCoordinator={isCoordinator}
					isEnabledToView={isEnabledToView}
				/>
			),
		},
		// 	]
		// : []),
	];
	return (
		<>
			<OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Modifica</Tooltip>}>
				<span>
					<Button
						variant="link"
						onClick={() => {
							setIsModalOpen(true);
						}}
					>
						<IcoEdit className="second-color ico-small edit" />
					</Button>
				</span>
			</OverlayTrigger>
			<Modal
				size="lg"
				show={isModalOpen}
				onHide={() => {
					setIsModalOpen(false);
					fetchTotals();
					fetchData();
				}}
				fullscreen
				className="modal-with-nav"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title style={{ flexBasis: '40%' }}>
						<DataHeader
							data={headerElements}
							customStyle={{
								margin: 0,
								paddingTop: '5px',
								paddingBottom: 0,
								marginLeft: '15px',
							}}
						/>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Tabs className="mb-4" variant="pills" activeKey={activeKey} onSelect={(key) => setActiveKey(key)} fill>
						{tabs.map((tab) => (
							<Tab key={tab.eventKey} eventKey={tab.eventKey} title={tab.title}>
								{activeKey === tab.eventKey ? tab.content : null}
							</Tab>
						))}
					</Tabs>
				</Modal.Body>
				{state === 'DRAFT' ? (
					<Modal.Footer className="justify-content-center">
						{isCoordinator || isEnabledToView ? (
							activeKey !== 'records' ? (
								<PeriodicReportingValidateModal
									data={data}
									fetchData={() => {
										fetchAmounts();
										fetchData();
									}}
								/>
							) : (
								<PeriodicReportingSettleModal
									data={data}
									fetchData={() => {
										fetchAmounts();
										fetchData();
									}}
									isModalOpen={isSettleAllModalOpen}
									setIsModalOpen={setIsSettleAllModalOpen}
									selectedIds={selectedIds}
									entity={entity}
									invoiceId={invoiceId}
									rc={rc}
								/>
							)
						) : null}
					</Modal.Footer>
				) : null}
			</Modal>
		</>
	);
};

export default PeriodicReportingUpdateModal;
